import React, {useState} from 'react';
import {Accordion} from '../../Accordion/Accordion';
import {CheckboxStyled, ContentWrapper} from './FileMetaDataTabAdditional.style';
import {t} from 'i18next';
import {generateIdentifier} from '../../../utils/IdentityUtils';
import {getDateStart, getEffectiveDate, isValidBoolean, isValidStringArray} from '../../../utils';
import {RangeDatePicker} from '../../DatePicker/RangeDatePicker';
import {TagPicker} from '../../TagPicker/TagPicker';
import EventEmitter from 'events';
import {
    FileMetaDataDialog_changeDateValidFrom,
    FileMetaDataDialog_changeDateValidTo,
    FileMetaDataDialog_changeTags,
    FileMetaDataDialog_changeWatermark
} from '../../../init/events';
import {TemplatePropertySettings} from '../../../models/TemplatePropertySettings';
import {isMandatoryComponent, isVisibleComponent} from '../../../utils/TemplateUtils';
import {
    STORAGE_OBJECT_OBJECT_METADATA,
    STORAGE_OBJECT_OBJECT_METADATA_DATE_VALID_FROM,
    STORAGE_OBJECT_OBJECT_METADATA_DATE_VALID_TO,
    STORAGE_OBJECT_OBJECT_METADATA_TAGS,
    STORAGE_OBJECT_OBJECT_METADATA_WATERMARK
} from '../../../init/storage_object_constants';
import {Valid} from '../../validation/Valid';

export interface FileMetaDataTabAdditionalProps {
    readonly templatePropertySettings: Map<string, TemplatePropertySettings>;
    readonly dateValidFrom?: number;
    readonly dateValidTo?: number;
    readonly tags?: string[];
    readonly isWatermarkApplicable: boolean;
    readonly watermark: boolean;
    readonly eventEmitter: EventEmitter;
}

/**
 * @name FileMetaDataTabAdditional
 * @description
 * The dialog to add the metadata of a newly created file or edited file - Metadata Tabs
 */
export const FileMetaDataTabAdditional: React.VFC<FileMetaDataTabAdditionalProps> = (props) => {
    const [dateValidFromInput, setDateValidFromInput] = useState<Date>(props.dateValidFrom ? new Date(props.dateValidFrom) : getEffectiveDate());
    const [dateValidToInput, setDateValidToInput] = useState<Date | undefined>(props.dateValidTo ? new Date(props.dateValidTo) : undefined);
    const [tagsInput, setTagsInput] = useState<string[]>(props.tags ?? []);
    const [watermarkInput, setWatermarkInput] = useState<boolean>(props.watermark ?? false);

    const handleDateRange = (dateValidFrom?: Date, dateValidTo?: Date) => {
        if ((dateValidFrom !== undefined) && dateValidFrom) {
            dateValidFrom = getDateStart(dateValidFrom);
            if (dateValidFromInput !== dateValidFrom) {
                setDateValidFromInput(dateValidFrom);
                props.eventEmitter.emit(FileMetaDataDialog_changeDateValidFrom, dateValidFrom);
            }
        }

        if ((dateValidTo !== undefined) && dateValidTo) {
            dateValidTo = getDateStart(dateValidTo);
            if (dateValidToInput !== dateValidTo) {
                setDateValidToInput(dateValidTo);
                props.eventEmitter.emit(FileMetaDataDialog_changeDateValidTo, dateValidTo);
            }
        } else {
            setDateValidToInput(undefined);
            props.eventEmitter.emit(FileMetaDataDialog_changeDateValidTo, undefined);
        }
    };

    const handleTags = (tags: string[]) => {
        setTagsInput(tags);
        props.eventEmitter.emit(FileMetaDataDialog_changeTags, tags);
    };

    const handleWatermarkInput = () => {
        setWatermarkInput(!watermarkInput);
        props.eventEmitter.emit(FileMetaDataDialog_changeWatermark, (!watermarkInput));
    };

    return (
        <ContentWrapper id={generateIdentifier()}>
            {(isVisibleComponent(STORAGE_OBJECT_OBJECT_METADATA, props.templatePropertySettings)
                    && (isVisibleComponent(STORAGE_OBJECT_OBJECT_METADATA_DATE_VALID_FROM, props.templatePropertySettings)
                        || isVisibleComponent(STORAGE_OBJECT_OBJECT_METADATA_DATE_VALID_TO, props.templatePropertySettings))) &&
                <Accordion accordionHeadline={t('VALIDITY_CAPTION')}
                           startOpen>
                    <RangeDatePicker templatePropertySettings={props.templatePropertySettings}
                                     dateFrom={dateValidFromInput}
                                     labelFrom={'DATE_FROM'}
                                     initializeFrom={true}
                                     isClearableFrom={true}
                                     isNullableFrom={false}
                                     dateTo={dateValidToInput}
                                     labelTo={'DATE_TO'}
                                     initializeTo={false}
                                     isClearableTo={true}
                                     isNullableTo={true}
                                     onChange={handleDateRange}/>
                </Accordion>
            }
            {(isVisibleComponent(STORAGE_OBJECT_OBJECT_METADATA, props.templatePropertySettings)
                    && isVisibleComponent(STORAGE_OBJECT_OBJECT_METADATA_TAGS, props.templatePropertySettings)) &&
                <Accordion accordionHeadline={t('UPLOAD_FILE_META_DATA_DIALOG_ACCORDION_TITLE_TAGS')}
                           startOpen>
                    <Valid
                        isValid={(isMandatoryComponent(STORAGE_OBJECT_OBJECT_METADATA_TAGS, props.templatePropertySettings) ? isValidStringArray(tagsInput) : true)}
                        message={t('ERROR_TAGS_MISSING')}>
                        <TagPicker dataSource={tagsInput}
                                   onChange={handleTags}/>
                    </Valid>
                </Accordion>
            }
            {props.isWatermarkApplicable &&
                <Accordion accordionHeadline={t('UPLOAD_FILE_META_DATA_DIALOG_ACCORDION_WATERMARK')}
                           startOpen>
                    <Valid
                        isValid={(isMandatoryComponent(STORAGE_OBJECT_OBJECT_METADATA_WATERMARK, props.templatePropertySettings) ? isValidBoolean(watermarkInput) : true)}
                        message={t('ERROR_WATERMARKING_ENABLED_MISSING')}>
                        <CheckboxStyled id={generateIdentifier('watermark')}
                                        label={t('UPLOAD_FILE_META_DATA_DIALOG_CHECKBOX_WATERMARK')}
                                        checked={watermarkInput}
                                        onChange={handleWatermarkInput}/>
                    </Valid>
                </Accordion>
            }
        </ContentWrapper>
    );
}